import React, { useEffect, useState } from 'react';
import { Box, TextField, FormControl, InputLabel, Select, MenuItem, Typography, FormHelperText } from '@mui/material';

const VehicleIncidentForm = ({ onFormChange = () => {}, incidentId = null, loading = false, error = null, incidentType = '' }) => {
  const [form, setForm] = useState({
    vehicleType: '',
    vehicleOther: '',
    thirdPartyInvolved: 'No',
    thirdPartyName: '',
    thirdPartyLicensePlate: '',
    thirdPartyInsurance: '',
    reportedToPoliceVehicle: 'No',
    policeReportDateVehicle: ''
  });

  const [errors, setErrors] = useState({});

  // Notify parent whenever the local form changes
  useEffect(() => {
    onFormChange(form);
    // validate when third party is required
    const newErrors = {};
    if (form.thirdPartyInvolved === 'Yes') {
      if (!form.thirdPartyName || form.thirdPartyName.trim() === '') newErrors.thirdPartyName = 'Required';
      if (!form.thirdPartyLicensePlate || form.thirdPartyLicensePlate.trim() === '') newErrors.thirdPartyLicensePlate = 'Required';
      if (!form.thirdPartyInsurance || form.thirdPartyInsurance.trim() === '') newErrors.thirdPartyInsurance = 'Required';
    }
    setErrors(newErrors);
  }, [form, onFormChange]);

  const handleChange = (field) => (e) => {
    const value = e?.target?.value ?? e;
    // If vehicle type changed away from "Other", clear the other text
    if (field === 'vehicleType' && value !== 'Other') {
      setForm(prev => ({ ...prev, vehicleType: value, vehicleOther: '' }));
    } else if (field === 'thirdPartyInvolved' && value === 'No') {
      // Clear third party fields when set to No
      setForm(prev => ({ ...prev, thirdPartyInvolved: value, thirdPartyName: '', thirdPartyLicensePlate: '', thirdPartyInsurance: '' }));
    } else {
      setForm(prev => ({ ...prev, [field]: value }));
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6">Vehicle Incident Details</Typography>

      <FormControl fullWidth>
        <InputLabel id="vehicle-type-label">Vehicle Type</InputLabel>
        <Select
          labelId="vehicle-type-label"
          value={form.vehicleType}
          label="Vehicle Type"
          onChange={handleChange('vehicleType')}
        >
          <MenuItem value="">Select</MenuItem>
          <MenuItem value="Car">Car</MenuItem>
          <MenuItem value="Truck">Truck</MenuItem>
          <MenuItem value="Van">Van</MenuItem>
          <MenuItem value="Motorcycle">Motorcycle</MenuItem>
          <MenuItem value="Other">Other</MenuItem>
        </Select>
      </FormControl>

      {form.vehicleType === 'Other' && (
        <TextField
          label="Vehicle (other)"
          value={form.vehicleOther}
          onChange={handleChange('vehicleOther')}
          fullWidth
        />
      )}

      <FormControl fullWidth>
        <InputLabel id="third-party-involved-label">3rd Party Involved</InputLabel>
        <Select
          labelId="third-party-involved-label"
          value={form.thirdPartyInvolved}
          label="3rd Party Involved"
          onChange={handleChange('thirdPartyInvolved')}
        >
          <MenuItem value="No">No</MenuItem>
          <MenuItem value="Yes">Yes</MenuItem>
        </Select>
        <FormHelperText>Defaults to No. If Yes, name, license plate and insurance are required.</FormHelperText>
      </FormControl>

      {form.thirdPartyInvolved === 'Yes' && (
        <>
          <TextField
            required
            label="3rd Party Name"
            value={form.thirdPartyName}
            onChange={handleChange('thirdPartyName')}
            error={Boolean(errors.thirdPartyName)}
            helperText={errors.thirdPartyName}
            fullWidth
          />

          <TextField
            required
            label="License Plate"
            value={form.thirdPartyLicensePlate}
            onChange={handleChange('thirdPartyLicensePlate')}
            error={Boolean(errors.thirdPartyLicensePlate)}
            helperText={errors.thirdPartyLicensePlate}
            fullWidth
          />

          <TextField
            required
            label="Insurance"
            value={form.thirdPartyInsurance}
            onChange={handleChange('thirdPartyInsurance')}
            error={Boolean(errors.thirdPartyInsurance)}
            helperText={errors.thirdPartyInsurance}
            fullWidth
          />
        </>
      )}

      <FormControl fullWidth>
        <InputLabel id="reported-to-police-vehicle-label">Reported to Police?</InputLabel>
        <Select
          labelId="reported-to-police-vehicle-label"
          value={form.reportedToPoliceVehicle}
          label="Reported to Police?"
          onChange={handleChange('reportedToPoliceVehicle')}
        >
          <MenuItem value="No">No</MenuItem>
          <MenuItem value="Yes">Yes</MenuItem>
        </Select>
      </FormControl>

      {form.reportedToPoliceVehicle === 'Yes' && (
        <TextField
          label="Police Report Date"
          type="date"
          value={form.policeReportDateVehicle}
          onChange={handleChange('policeReportDateVehicle')}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
      )}

      {error && (
        <Typography color="error" variant="body2">{String(error)}</Typography>
      )}

      <Typography variant="caption">This form is saved automatically as part of the incident submission (no separate Save button).</Typography>
    </Box>
  );
};

export default VehicleIncidentForm;
